import { X, Shuffle, RotateCcw, Sparkles } from "lucide-react";
import { useState, useEffect } from "react";

const PUZZLE_IMAGES = [
  "https://customer-assets.emergentagent.com/job_fluffy-friend-4/artifacts/tfoei384_puzzle01.png",
  "https://customer-assets.emergentagent.com/job_fluffy-friend-4/artifacts/ac1t8h17_puzzle02.png",
  "https://customer-assets.emergentagent.com/job_fluffy-friend-4/artifacts/anuyb5lb_puzzle03.png",
  "https://customer-assets.emergentagent.com/job_fluffy-friend-4/artifacts/gk0qdu80_puzzle04.png",
  "https://customer-assets.emergentagent.com/job_fluffy-friend-4/artifacts/17b7u5c9_puzzle05.png"
];

const PuzzleGame = ({ isOpen, onClose }) => {
  const [gridSize, setGridSize] = useState(3);
  const [tiles, setTiles] = useState([]);
  const [moves, setMoves] = useState(0);
  const [time, setTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSolved, setIsSolved] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const initializePuzzle = () => {
    const totalTiles = gridSize * gridSize;
    const newTiles = Array.from({ length: totalTiles }, (_, i) => i);
    setTiles(newTiles);
    setMoves(0);
    setTime(0);
    setIsPlaying(false);
    setIsSolved(false);
  };

  const shufflePuzzle = () => {
    const totalTiles = gridSize * gridSize;
    let newTiles = Array.from({ length: totalTiles }, (_, i) => i);
    for (let i = newTiles.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newTiles[i], newTiles[j]] = [newTiles[j], newTiles[i]];
    }
    setTiles(newTiles);
    setMoves(0);
    setTime(0);
    setIsPlaying(true);
    setIsSolved(false);
  };

  const checkSolved = (currentTiles) => {
    for (let i = 0; i < currentTiles.length - 1; i++) {
      if (currentTiles[i] !== i) return false;
    }
    return currentTiles[currentTiles.length - 1] === currentTiles.length - 1;
  };

  const handleTileClick = (index) => {
    if (!isPlaying || isSolved) return;
    const emptyIndex = tiles.indexOf(gridSize * gridSize - 1);
    const row = Math.floor(index / gridSize);
    const col = index % gridSize;
    const emptyRow = Math.floor(emptyIndex / gridSize);
    const emptyCol = emptyIndex % gridSize;
    const isAdjacent = 
      (Math.abs(row - emptyRow) === 1 && col === emptyCol) ||
      (Math.abs(col - emptyCol) === 1 && row === emptyRow);
    
    if (isAdjacent) {
      const newTiles = [...tiles];
      [newTiles[index], newTiles[emptyIndex]] = [newTiles[emptyIndex], newTiles[index]];
      setTiles(newTiles);
      setMoves(m => m + 1);
      if (checkSolved(newTiles)) {
        setIsSolved(true);
        setIsPlaying(false);
      }
    }
  };

  useEffect(() => {
    let interval;
    if (isPlaying && !isSolved) {
      interval = setInterval(() => setTime(t => t + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, isSolved]);

  useEffect(() => {
    if (isOpen) initializePuzzle();
  }, [isOpen, gridSize]);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % PUZZLE_IMAGES.length);
    initializePuzzle();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  const puzzleImage = PUZZLE_IMAGES[currentImage];

  return (
    <div 
      className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[80] overflow-y-auto"
      data-testid="puzzle-game-container"
    >
      <div className="min-h-full flex items-center justify-center p-4 py-6">
      <div className="bg-[#FDFBF7] rounded-3xl p-4 sm:p-6 max-w-lg w-full text-center shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 text-[#6B6B6B] hover:text-[#4A4A4A] transition-colors"
          data-testid="puzzle-close-button"
        >
          <X className="w-7 h-7" />
        </button>
        
        <h2 className="text-xl sm:text-2xl font-bold text-[#4A4A4A] mb-3">Poodle Puzzle</h2>
        
        <div className="flex justify-center gap-1 sm:gap-2 mb-3 flex-wrap">
          {[3, 4, 5, 6].map(size => (
            <button
              key={size}
              onClick={() => setGridSize(size)}
              className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-bold transition-all ${
                gridSize === size 
                  ? 'bg-[#FFF7D6] text-[#B89823] border-2 border-[#FCEB9C]' 
                  : 'bg-[#F3F0E6] text-[#6B6B6B] border-2 border-transparent hover:bg-[#E8E5DC]'
              }`}
            >
              {size}x{size}
            </button>
          ))}
        </div>
        
        <div className="flex justify-center gap-6 mb-3">
          <div className="text-center">
            <p className="text-xs text-[#9A9A9A]">Moves</p>
            <p className="text-lg font-bold text-[#4A4A4A]">{moves}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-[#9A9A9A]">Time</p>
            <p className="text-lg font-bold text-[#4A4A4A]">{formatTime(time)}</p>
          </div>
        </div>
        
        <div 
          className="relative mx-auto mb-3 bg-[#E8E5DC] p-1"
          style={{ width: 'min(420px, 75vw, 70vh)', height: 'min(420px, 75vw, 70vh)' }}
        >
          <div 
            className="grid w-full h-full"
            style={{ gridTemplateColumns: `repeat(${gridSize}, 1fr)`, gap: '1px' }}
          >
            {tiles.map((tile, index) => {
              const isEmptyTile = tile === gridSize * gridSize - 1;
              const tileRow = Math.floor(tile / gridSize);
              const tileCol = tile % gridSize;
              const bgPosX = gridSize > 1 ? (tileCol / (gridSize - 1)) * 100 : 0;
              const bgPosY = gridSize > 1 ? (tileRow / (gridSize - 1)) * 100 : 0;
              
              return (
                <button
                  key={index}
                  onClick={() => handleTileClick(index)}
                  disabled={isEmptyTile || !isPlaying}
                  className={`relative overflow-hidden transition-all duration-100 ${
                    isEmptyTile ? 'bg-[#D0CCC0]' : 'bg-white hover:brightness-105 active:scale-95'
                  }`}
                  style={{
                    backgroundImage: isEmptyTile ? 'none' : `url(${puzzleImage})`,
                    backgroundSize: `${gridSize * 100}% ${gridSize * 100}%`,
                    backgroundPosition: isEmptyTile ? undefined : `${bgPosX}% ${bgPosY}%`,
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  {!isEmptyTile && isPlaying && (
                    <span className={`absolute bottom-0.5 right-0.5 bg-black/30 text-white/70 px-1 rounded font-bold ${
                      gridSize >= 6 ? 'text-[6px]' : gridSize >= 5 ? 'text-[7px]' : 'text-[8px] sm:text-[10px]'
                    }`}>
                      {tile + 1}
                    </span>
                  )}
                  {!isPlaying && !isEmptyTile && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/15 text-white/80 font-bold text-sm sm:text-base">
                      {tile + 1}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          {isSolved && (
            <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center">
              <p className="text-2xl font-bold text-[#5A8A5A] mb-2">You did it! 🎉</p>
              <p className="text-base text-[#6B6B6B]">{moves} moves in {formatTime(time)}</p>
            </div>
          )}
        </div>
        
        <div className="flex justify-center gap-2 flex-wrap">
          <button onClick={shufflePuzzle} className="flex items-center gap-1.5 px-4 py-2 bg-[#FFF7D6] text-[#B89823] rounded-full text-sm font-bold hover:bg-[#FCEB9C] transition-all hover:scale-105">
            <Shuffle className="w-4 h-4" /> Shuffle
          </button>
          <button onClick={initializePuzzle} className="flex items-center gap-1.5 px-4 py-2 bg-[#E6F0F9] text-[#4A7AA5] rounded-full text-sm font-bold hover:bg-[#C9DFE9] transition-all hover:scale-105">
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
          <button onClick={nextImage} className="flex items-center gap-1.5 px-4 py-2 bg-[#FCEAEA] text-[#A55B64] rounded-full text-sm font-bold hover:bg-[#F3C4C4] transition-all hover:scale-105">
            <Sparkles className="w-4 h-4" /> New Picture
          </button>
        </div>
        
        <p className="text-xs text-[#9A9A9A] mt-3">
          Click "Shuffle" to start, then slide the tiles! Numbers show the correct order.
        </p>
      </div>
      </div>
    </div>
  );
};

export default PuzzleGame;
